import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaExchangeAlt } from 'react-icons/fa';
import BeforeAfterModal from './BeforeAfterModal';
import './BeforeAfterGallery.css';

const BeforeAfterGallery = ({ beforeAfterPairs, isLoading = false }) => {
  const [selectedPair, setSelectedPair] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [visibleCount, setVisibleCount] = useState(6);
  const [isMobile, setIsMobile] = useState(false);
  const scrollPositionRef = useRef(0);

  const openModal = useCallback((pair, index, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Save scroll position immediately
    scrollPositionRef.current = window.scrollY;
    
    setSelectedPair(pair);
    setCurrentPairIndex(index);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsModalOpen(false);
    setSelectedPair(null);
  }, []);

  // Handle modal open/close with scroll lock
  useEffect(() => {
    if (isModalOpen) {
      // Prevent body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Store the scroll position to restore
        const scrollY = scrollPositionRef.current;
        
        // Temporarily disable smooth scrolling
        const htmlElement = document.documentElement;
        const originalScrollBehavior = htmlElement.style.scrollBehavior;
        htmlElement.style.scrollBehavior = 'auto';
        
        // Remove fixed positioning from body
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Restore scroll position instantly
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
          
          // Restore smooth scrolling
          requestAnimationFrame(() => {
            htmlElement.style.scrollBehavior = originalScrollBehavior;
          });
        });
      };
    }
  }, [isModalOpen]);

  // Detect screen size for responsive item limiting
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobileScreen = window.innerWidth <= 480;
      
      // Only reset visibleCount if we're transitioning between mobile and desktop
      // This prevents resetting when scrolling causes resize events on mobile
      if (isMobileScreen !== isMobile) {
        setIsMobile(isMobileScreen);
        setVisibleCount(isMobileScreen ? 3 : 6);
      }
    };

    // Initial check - set both states on first load
    const initialIsMobile = window.innerWidth <= 480;
    setIsMobile(initialIsMobile);
    setVisibleCount(initialIsMobile ? 3 : 6);

    // Add resize listener
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, [isMobile]);

  // Get visible pairs
  const visiblePairs = useMemo(() => 
    beforeAfterPairs.slice(0, visibleCount), 
    [beforeAfterPairs, visibleCount]
  );

  const hasMorePairs = useMemo(() => 
    visibleCount < beforeAfterPairs.length, 
    [visibleCount, beforeAfterPairs.length]
  );

  const handleShowMore = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Store current scroll position and visible count before update
    const currentScrollY = window.scrollY;
    const previousVisibleCount = visibleCount;
    
    setVisibleCount(prev => Math.min(prev + (isMobile ? 3 : 6), beforeAfterPairs.length));
    
    // Focus on first newly added image after state update
    // Use setTimeout to ensure DOM has fully updated
    setTimeout(() => {
      window.scrollTo(0, currentScrollY);
      
      // Find all gallery items within the before-after-gallery
      const allCards = document.querySelectorAll('.before-after-gallery .gallery-item');
      
      // Get the first newly added card (at index previousVisibleCount)
      const newlyAddedCard = allCards[previousVisibleCount];
      
      if (newlyAddedCard) {
        // The gallery-item itself is already focusable (has tabIndex={0})
        newlyAddedCard.focus({ preventScroll: true });
        // Scroll the element into view smoothly
        newlyAddedCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  }, [isMobile, beforeAfterPairs.length, visibleCount]);

  if (isLoading) {
    return (
      <div className="before-after-gallery-loading">
        <div className="loading-spinner"></div>
        <p>Loading before/after pairs...</p>
      </div>
    );
  }

  if (!beforeAfterPairs || beforeAfterPairs.length === 0) {
    return (
      <div className="before-after-gallery-empty">
        <FaExchangeAlt className="empty-icon" />
        <p>No before/after pairs available</p>
      </div>
    );
  }

  return (
    <>
      <div className="before-after-gallery">
        <div className="gallery-grid">
          {visiblePairs.map((pair, index) => (
            <div 
              key={pair.id} 
              className="gallery-item"
              onClick={(e) => openModal(pair, index, e)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openModal(pair, index, e);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`View before and after comparison for project ${pair.projectNumber}`}
            >
              <div className="item-image-container">
                <img
                  src={pair.after.src}
                  alt={pair.after.alt}
                  className="item-image"
                  loading="lazy"
                  decoding="async"
                  onLoad={() => setLoadedImages(prev => new Set([...prev, pair.id]))}
                  onError={() => setLoadedImages(prev => new Set([...prev, pair.id]))}
                  style={{
                    opacity: loadedImages.has(pair.id) ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    willChange: 'opacity'
                  }}
                />
                <div className="item-overlay">
                  <FaExchangeAlt className="overlay-icon" />
                  <span className="overlay-text">View Comparison</span>
                </div>
                <div className="item-badge">
                ★
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button */}
        {hasMorePairs && (
          <div className="show-more-section">
            <button type="button" className="show-more-btn" onClick={handleShowMore}>
              Show More Transformations
              <span className="show-more-count">
                ({beforeAfterPairs.length - visibleCount} remaining)
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Before/After Modal */}
      <BeforeAfterModal
        isOpen={isModalOpen}
        onClose={closeModal}
        selectedPair={selectedPair}
      />
    </>
  );
};

export default BeforeAfterGallery;
